// Pure event model of the calendar: turns GET /api/calendar ({routines: occurrences, tasks}) into
// display events, rebuilds the same shape locally when the endpoint is missing, filters (departments,
// ROUTINES / FAITES toggles, search, one routine), groups by day and computes the "N de plus"
// overflow of a month cell. DOM-free.
import { t } from '../../i18n/fr.js';
import { dayKey } from './grid.js';
import { toMs, matchesQuery, officeIndex } from '../automations/model.js';
import { occurrences } from '../automations/schedule.js';
import './strings.js';

export const DONE_STATES = new Set(['done', 'failed', 'rejected', 'cancelled']);
export const ACTIVE_STATES = new Set(['queued', 'running', 'awaiting_approval', 'executing']);
/** Order of kinds at the same minute: routines, then scheduled, running, done. */
const KIND_ORDER = { routine: 0, scheduled: 1, active: 2, done: 3 };
/** A routine occurrence this far in the past is replaced by what really ran (its task). */
const PAST_GRACE_MS = 60 * 1000;

const pad2 = (n) => String(n).padStart(2, '0');

/** "HH:MM" of an instant. */
export function hhmm(ms) {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * The instant a task is shown at, and its kind.
 * @param {object} task TaskSummary
 * @returns {{at: number|null, kind: 'scheduled'|'active'|'done'}}
 */
export function taskPlacement(task) {
  const state = task?.state;
  if (state === 'scheduled') return { at: toMs(task.dueAt), kind: 'scheduled' };
  if (DONE_STATES.has(state)) return { at: toMs(task.finishedAt) ?? toMs(task.updatedAt), kind: 'done' };
  return { at: toMs(task?.dueAt) ?? toMs(task?.startedAt) ?? toMs(task?.createdAt), kind: 'active' };
}

/**
 * Display events from the calendar API response.
 * @param {{routines?: object[], tasks?: object[]}} data
 * @param {{from?: number, to?: number, now?: number, hidePastRoutines?: boolean}} [opts]
 * @returns {Array<object>} events {id, kind, at, day, title, floorId, deptId, agentId, routineId?, taskId?,
 *   state?, paused?, description?, late?, routineSource?, resolved?} — `resolved` for a failure or a
 *   blocking nobody has to deal with: 'superseded' (a later run of its routine went well), 'ignored'
 *   (a person ignored it) or 'settled' (marked « réglé »); idle routine passes are left out.
 */
export function normalizeCalendar(data, { from = -Infinity, to = Infinity, now = Date.now(), hidePastRoutines = true } = {}) {
  const out = [];
  const seen = new Set();
  for (const r of Array.isArray(data?.routines) ? data.routines : []) {
    if (!r || typeof r !== 'object') continue;
    const at = toMs(r.at);
    const routineId = r.routineId ?? r.id;
    if (at == null || !routineId || at < from || at >= to) continue;
    if (hidePastRoutines && at < now - PAST_GRACE_MS) continue;
    const id = `r:${routineId}:${at}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      id, kind: 'routine', at, day: dayKey(at), title: String(r.title || ''), floorId: r.floorId, deptId: r.deptId,
      agentId: r.agentId, routineId, paused: !!r.paused, description: r.description ? String(r.description) : '',
    });
  }
  for (const tk of Array.isArray(data?.tasks) ? data.tasks : []) {
    if (!tk || typeof tk !== 'object' || !tk.id || tk.idle === true) continue;
    const { at, kind } = taskPlacement(tk);
    if (at == null || at < from || at >= to) continue;
    const id = `t:${tk.id}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      id, kind, at, day: dayKey(at), title: String(tk.title || tk.inputPreview || ''), floorId: tk.floorId,
      deptId: tk.deptId, agentId: tk.agentId, agentName: tk.agentName, taskId: tk.id, state: tk.state, blocked: tk.state === 'done' && !!tk.blocked,
      late: !!tk.late, routineSource: tk.source?.routineId || null, sourceKind: tk.source?.kind || null,
      resolved: tk.superseded ? 'superseded' : tk.ignored ? 'ignored' : tk.settled ? 'settled' : null,
    });
  }
  return sortEvents(out);
}

/** Events sorted by time, then kind, then title. Returns a new array. */
export function sortEvents(list) {
  return [...list].sort((a, b) => a.at - b.at || (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9)
    || String(a.title).localeCompare(String(b.title), 'fr'));
}

/**
 * Rebuilds the calendar API response from GET /api/routines and the tasks the browser knows, for
 * servers where /api/calendar is missing. Occurrences use the local mirror of the schedule maths.
 * @param {{routines: object[], tasks: Iterable<object>, from: number, to: number, floorId?: string}} input
 * @returns {{routines: object[], tasks: object[]}}
 */
export function calendarFallback({ routines = [], tasks = [], from, to, floorId = '' }) {
  const occ = [];
  for (const r of routines) {
    if (!r?.schedule || (floorId && r.floorId !== floorId)) continue;
    for (const at of occurrences(r.schedule, from, to, 400)) {
      occ.push({ routineId: r.id, title: r.title, agentId: r.agentId, floorId: r.floorId, deptId: r.deptId, at, paused: !!r.paused });
    }
  }
  const list = [];
  for (const tk of tasks) {
    if (!tk || (floorId && tk.floorId !== floorId)) continue;
    const { at } = taskPlacement(tk);
    if (at != null && at >= from && at < to) list.push(tk);
  }
  return { routines: occ, tasks: list };
}

/**
 * Filters events for display.
 * @param {object[]} events
 * @param {{deptIds?: Iterable<string>, showRoutines?: boolean, showDone?: boolean, query?: string,
 *   routineId?: string|null, office?: object}} [opts]
 */
export function filterEvents(events, { deptIds = [], showRoutines = true, showDone = true, query = '', routineId = null, office = null } = {}) {
  const depts = new Set(deptIds);
  const idx = office ? officeIndex(office) : null;
  return events.filter((ev) => {
    if (depts.size && !depts.has(ev.deptId)) return false;
    if (!showRoutines && ev.kind === 'routine') return false;
    if (!showDone && ev.kind === 'done') return false;
    if (routineId && ev.routineId !== routineId && ev.routineSource !== routineId) return false;
    if (query) {
      const agent = idx?.agents.get(ev.agentId);
      const dept = idx?.depts.get(ev.deptId);
      if (!matchesQuery([ev.title, ev.description, ev.agentName, agent?.name, agent?.role, dept?.name], query)) return false;
    }
    return true;
  });
}

/**
 * Events by day key, each list sorted.
 * @param {object[]} events
 * @returns {Map<string, object[]>}
 */
export function groupByDay(events) {
  const map = new Map();
  for (const ev of sortEvents(events)) {
    if (!map.has(ev.day)) map.set(ev.day, []);
    map.get(ev.day).push(ev);
  }
  return map;
}

/**
 * What fits in a month cell holding `capacity` rows: every event when they fit, otherwise the
 * first `capacity - 1` and a "N de plus" row for the rest.
 * @param {object[]} list
 * @param {number} capacity rows available (≥ 1)
 * @returns {{visible: object[], more: number}}
 */
export function dayOverflow(list, capacity) {
  const cap = Math.max(1, Math.floor(Number(capacity) || 1));
  if (list.length <= cap) return { visible: list, more: 0 };
  const shown = Math.max(0, cap - 1);
  return { visible: list.slice(0, shown), more: list.length - shown };
}

/** Is this event a failure or a blocking nobody has dealt with (« en erreur »)? */
export function isOpenError(ev) {
  return ev?.kind === 'done' && (ev.state === 'failed' || !!ev.blocked) && !ev.resolved;
}

/**
 * Counts of the header: planned runs of the active routines (paused ones apart), scheduled tasks,
 * finished tasks (done, neither blocked nor a failure resolved later) and the open errors (failed
 * or blocked, not resolved by a later run, ignored or settled).
 * @param {object[]} events
 * @returns {{routines: number, paused: number, scheduled: number, done: number, errors: number}}
 */
export function summarize(events) {
  const out = { routines: 0, paused: 0, scheduled: 0, done: 0, errors: 0 };
  for (const ev of events) {
    if (ev.kind === 'routine') out[ev.paused ? 'paused' : 'routines'] += 1;
    else if (ev.kind === 'scheduled') out.scheduled += 1;
    else if (ev.kind === 'done' && ev.state === 'done' && !ev.blocked) out.done += 1;
    if (isOpenError(ev)) out.errors += 1;
  }
  return out;
}

/**
 * The small line under an event title: "08:00", "10:00 · programmée", "faite 20:20", "échec 14:02".
 * @param {object} ev
 * @returns {string}
 */
export function eventCaption(ev) {
  const time = hhmm(ev.at);
  if (ev.kind === 'routine') return t(ev.paused ? 'cal.ev.routinePaused' : 'cal.ev.routine', { time });
  if (ev.kind === 'scheduled') return t(ev.late ? 'cal.ev.late' : 'cal.ev.scheduled', { time });
  if (ev.kind === 'done' && ev.resolved && (ev.state === 'failed' || ev.blocked)) return t(`cal.ev.${ev.resolved}`, { time });
  if (ev.kind === 'done') return t(`cal.ev.${ev.blocked ? 'blocked' : DONE_STATES.has(ev.state) ? ev.state : 'done'}`, { time });
  return t(`cal.ev.${ACTIVE_STATES.has(ev.state) ? ev.state : 'queued'}`, { time });
}

/**
 * Tone class of an event (for the left border and icon): routine | scheduled | active | done | blocked | failed | muted.
 * @param {object} ev
 */
export function eventTone(ev) {
  if (ev.kind === 'done') {
    // A failure or a blocking resolved since (later run, ignored, settled) is no alarm any more.
    if ((ev.state === 'failed' || ev.blocked) && ev.resolved) return 'muted';
    if (ev.state === 'failed') return 'failed';
    if (ev.blocked) return 'blocked';
    if (ev.state === 'rejected' || ev.state === 'cancelled') return 'muted';
    return 'done';
  }
  return ev.kind;
}

/**
 * Routines of the rail with the filters applied (floor, departments, search).
 * @param {object[]} routines
 * @param {{floorId?: string, deptIds?: Iterable<string>, query?: string, office?: object}} [opts]
 */
export function filterRoutines(routines, { floorId = '', deptIds = [], query = '', office = null } = {}) {
  const depts = new Set(deptIds);
  const idx = office ? officeIndex(office) : null;
  return routines.filter((r) => {
    if (floorId && r.floorId !== floorId) return false;
    if (depts.size && !depts.has(r.deptId)) return false;
    if (query) {
      const agent = idx?.agents.get(r.agentId);
      const dept = idx?.depts.get(r.deptId);
      if (!matchesQuery([r.title, r.prompt, r.description, agent?.name, dept?.name], query)) return false;
    }
    return true;
  });
}

/**
 * Does a live task event change what the calendar shows? True when the task is already on screen,
 * or when its due / finish time falls in the period shown.
 * @param {object} task TaskSummary from the 'task' event
 * @param {{from: number, to: number}} range
 * @param {Set<string>} knownTaskIds ids of the tasks currently shown
 * @returns {boolean}
 */
export function isRelevantTask(task, { from, to }, knownTaskIds = new Set()) {
  if (!task || typeof task !== 'object') return false;
  if (task.id && knownTaskIds.has(task.id)) return true;
  for (const v of [task.dueAt, task.finishedAt]) {
    const ms = toMs(v);
    if (ms !== null && ms >= from && ms < to) return true;
  }
  return false;
}

/** Smallest month row (px) that holds 2 or 3 event rows and the day number (calendar.css cap-N). */
export const MONTH_ROW_MIN = Object.freeze({ 2: 124, 3: 168 });

/**
 * Month-cell capacity (event rows before "N de plus"). With the height the rows can share (px):
 * 3 when every row gets MONTH_ROW_MIN[3], else 2, so the whole month fits on the screen. Without a
 * measure: 3 for 4–5 week months, 2 for 6 weeks.
 * @param {number} weeks
 * @param {number} [rowsHeight]
 */
export function monthCapacity(weeks, rowsHeight) {
  const avail = Number(rowsHeight);
  if (weeks > 0 && Number.isFinite(avail) && avail > 0) return avail / weeks >= MONTH_ROW_MIN[3] ? 3 : 2;
  return weeks >= 6 ? 2 : 3;
}
